import { Rectangle, Texture } from 'pixi.js';

/**
 * LabelAtlas — one canvas page, one texture source, for every changing world
 * label: floating combat text and the HP/MP numbers on bars (plan 16 §2.3).
 *
 * Why: in Pixi 8 a canvas `Text` re-rasterises and rebuilds the whole stage's
 * instruction set whenever its string changes, and a `BitmapText` redraws a
 * proxy Graphics (also a rebuild). A Sprite whose texture changes to another
 * frame of the SAME source does not rebuild (`Batcher.checkAndUpdateTexture`).
 * So text that changes is drawn as a run of sprites over one atlas page.
 *
 * Tokens: digits and number punctuation are single-glyph frames (any number is
 * then a handful of reused frames); every other run of characters ("Lightning
 * Bolt", "Immune") is one whole-word frame, rendered on first use. A frame is
 * white fill over a black stroke, so a sprite tint colours the fill and keeps
 * the outline black — the look of the old `Text` (fill colour, black stroke).
 *
 * Frames are drawn at `RES`× so they stay crisp under the camera zoom and the
 * crit punch. When the page fills up it is wiped and every live label lays
 * itself out again (one rebuild, rare); with no 2D canvas (jsdom) every token
 * maps to the shared white texture at its measured size.
 */

/** Atlas pixels per world px. */
export const LABEL_ATLAS_RES = 2;
const PAGE_PX = 1024;
const PAD = 2;
const GLYPHS = new Set('0123456789+-−/%.,:!'.split(''));

export interface LabelFontStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  strokeWidth: number;
}

export interface LabelFrame {
  texture: Texture;
  /** Frame size in world px at scale 1 (ink plus the stroke on both sides). */
  width: number;
  height: number;
  /** Pen advance in world px: the text's own width, as a canvas Text lays it out. */
  advance: number;
  /** Stroke overhang on each side in world px (the frame starts this far left of the pen). */
  pad: number;
}

/** A label laid out over the atlas — re-laid out when the page is wiped. */
export interface AtlasClient {
  relayout(): void;
}

/** Split a string into atlas tokens: number glyphs one by one, other runs whole. */
export function tokenizeLabel(text: string): string[] {
  const out: string[] = [];
  let word = '';
  for (const ch of text) {
    if (GLYPHS.has(ch)) {
      if (word) { out.push(word); word = ''; }
      out.push(ch);
    } else {
      word += ch;
    }
  }
  if (word) out.push(word);
  return out;
}

export function fontKey(s: LabelFontStyle): string {
  return `${s.fontWeight}|${s.fontSize}|${s.strokeWidth}|${s.fontFamily}`;
}

class LabelAtlasImpl {
  private ctx: CanvasRenderingContext2D | null = null;
  private texture: Texture | null = null;
  private frames = new Map<string, LabelFrame>();
  private clients = new Set<AtlasClient>();
  private shelfX = PAD;
  private shelfY = PAD;
  private shelfH = 0;
  private dirty = false;
  private inited = false;
  private resetting = false;
  /** Times the page was wiped and re-laid out (diagnostics/tests). */
  resets = 0;

  private init(): void {
    if (this.inited) return;
    this.inited = true;
    try {
      const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
      const ctx = canvas?.getContext('2d') ?? null;
      if (!canvas || !ctx) return;
      canvas.width = PAGE_PX;
      canvas.height = PAGE_PX;
      const texture = Texture.from(canvas);
      // A test double without a texture source cannot carry frames.
      if (!texture?.source || typeof Texture !== 'function') return;
      this.ctx = ctx;
      this.texture = texture;
      // Web fonts (Inter) may finish loading after the first frames were
      // drawn with a fallback face: redraw everything once they land.
      const fonts = (document as Document & { fonts?: EventTarget }).fonts;
      fonts?.addEventListener?.('loadingdone', () => this.reset());
    } catch {
      this.ctx = null;
      this.texture = null;
    }
  }

  register(client: AtlasClient): void { this.clients.add(client); }
  unregister(client: AtlasClient): void { this.clients.delete(client); }

  /** The frame for one token in one font, drawing it into the page if new. */
  frame(token: string, style: LabelFontStyle): LabelFrame {
    this.init();
    const key = `${fontKey(style)}|${token}`;
    const hit = this.frames.get(key);
    if (hit) return hit;
    let made = this.draw(token, style);
    if (!made && !this.resetting) {
      this.reset();
      made = this.draw(token, style);
    }
    made ??= this.whiteFrame(token, style);
    this.frames.set(key, made);
    return made;
  }

  /** A transparent frame of the page — the texture an unused slot carries. */
  blank(): Texture {
    this.init();
    if (!this.texture) return Texture.WHITE;
    const key = '__blank__';
    let f = this.frames.get(key);
    if (!f) {
      f = { texture: new Texture({ source: this.texture.source, frame: new Rectangle(0, 0, 1, 1) }), width: 0, height: 0, advance: 0, pad: 0 };
      this.frames.set(key, f);
    }
    return f.texture;
  }

  /** Upload pending drawing once the current task is done (draws coalesce). */
  private markDirty(): void {
    if (this.dirty) return;
    this.dirty = true;
    queueMicrotask(() => this.flush());
  }

  /** Upload pending drawing to the GPU. */
  flush(): void {
    if (!this.dirty || !this.texture) return;
    this.dirty = false;
    this.texture.source.update();
  }

  private metrics(style: LabelFontStyle): { font: string; h: number; baseline: number; stroke: number } {
    const px = style.fontSize * LABEL_ATLAS_RES;
    const stroke = style.strokeWidth * LABEL_ATLAS_RES;
    return {
      font: `${style.fontWeight} ${px}px ${style.fontFamily}`,
      h: Math.ceil(px * 1.2 + stroke),
      baseline: Math.round(px * 0.95 + stroke / 2),
      stroke,
    };
  }

  private draw(token: string, style: LabelFontStyle): LabelFrame | null {
    const ctx = this.ctx;
    if (!ctx || !this.texture) return null;
    const m = this.metrics(style);
    ctx.font = m.font;
    const advance = ctx.measureText(token).width;
    const w = Math.ceil(advance + m.stroke);
    if (w + PAD * 2 > PAGE_PX) return this.whiteFrame(token, style);
    if (this.shelfX + w + PAD > PAGE_PX) {
      this.shelfX = PAD;
      this.shelfY += this.shelfH + PAD;
      this.shelfH = 0;
    }
    if (this.shelfY + m.h + PAD > PAGE_PX) return null;
    const x = this.shelfX;
    const y = this.shelfY;
    this.shelfX += w + PAD;
    this.shelfH = Math.max(this.shelfH, m.h);

    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.lineJoin = 'round';
    if (m.stroke > 0) {
      ctx.lineWidth = m.stroke;
      ctx.strokeStyle = '#000000';
      ctx.strokeText(token, x + m.stroke / 2, y + m.baseline);
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillText(token, x + m.stroke / 2, y + m.baseline);
    this.markDirty();
    return {
      texture: new Texture({ source: this.texture.source, frame: new Rectangle(x, y, w, m.h) }),
      width: w / LABEL_ATLAS_RES,
      height: m.h / LABEL_ATLAS_RES,
      advance: advance / LABEL_ATLAS_RES,
      pad: m.stroke / 2 / LABEL_ATLAS_RES,
    };
  }

  private whiteFrame(token: string, style: LabelFontStyle): LabelFrame {
    const advance = token.length * style.fontSize * 0.6;
    return {
      texture: Texture.WHITE,
      width: advance + style.strokeWidth,
      height: style.fontSize * 1.2 + style.strokeWidth,
      advance,
      pad: style.strokeWidth / 2,
    };
  }

  /** Wipe the page and have every live label lay itself out again. */
  reset(): void {
    if (!this.ctx) return;
    this.resets++;
    this.ctx.clearRect(0, 0, PAGE_PX, PAGE_PX);
    this.frames.clear();
    this.shelfX = PAD;
    this.shelfY = PAD;
    this.shelfH = 0;
    this.markDirty();
    this.resetting = true;
    try {
      for (const c of Array.from(this.clients)) c.relayout();
    } finally {
      this.resetting = false;
    }
  }

  stats(): { frames: number; clients: number; resets: number; shelfY: number } {
    return { frames: this.frames.size, clients: this.clients.size, resets: this.resets, shelfY: this.shelfY };
  }
}

export const LabelAtlas = new LabelAtlasImpl();
